import React, { useState } from 'react';
import { Download, Filter, Calendar, TrendingUp, TrendingDown, AlertTriangle, Users, DollarSign, Clock, Target, BarChart3, PieChart, Activity } from 'lucide-react';

interface KPI {
  id: string;
  name: string;
  value: number;
  target: number;
  unit: string;
  trend: 'up' | 'down' | 'stable';
  changePercent: number;
  status: 'good' | 'warning' | 'critical';
  category: 'completion' | 'cost' | 'effectiveness' | 'satisfaction' | 'safety' | 'compliance';
}

interface DrillDownData {
  dimension: string;
  values: {
    name: string;
    value: number;
    percentage: number;
    trend: 'up' | 'down' | 'stable';
  }[];
}

interface Alert {
  id: string;
  type: 'budget_exceeded' | 'low_completion' | 'low_satisfaction' | 'schedule_delay' | 'certification_expiry' | 'safety_compliance';
  title: string;
  description: string;
  severity: 'high' | 'medium' | 'low';
  department: string;
  branch: string;
  date: string;
  status: 'active' | 'acknowledged' | 'resolved';
}

const ReportsPage: React.FC = () => {
  const [kpis, setKpis] = useState<KPI[]>([
    {
      id: '1',
      name: 'Tỷ lệ hoàn thành đào tạo An ninh',
      value: 94,
      target: 98,
      unit: '%',
      trend: 'up',
      changePercent: 3.2,
      status: 'warning',
      category: 'completion'
    },
    {
      id: '2',
      name: 'Chi phí đào tạo / nhân viên',
      value: 12500000,
      target: 15000000,
      unit: 'VND',
      trend: 'down',
      changePercent: -8.5,
      status: 'good',
      category: 'cost'
    },
    {
      id: '3',
      name: 'Điểm hài lòng dịch vụ đào tạo',
      value: 4.8,
      target: 4.5,
      unit: '/5',
      trend: 'up',
      changePercent: 4.3,
      status: 'good',
      category: 'satisfaction'
    },
    {
      id: '4',
      name: 'ROI đào tạo chuyên ngành',
      value: 215,
      target: 180,
      unit: '%',
      trend: 'up',
      changePercent: 12.8,
      status: 'good',
      category: 'effectiveness'
    },
    {
      id: '5',
      name: 'Thời gian cấp chứng chỉ TB',
      value: 28,
      target: 35,
      unit: 'ngày',
      trend: 'down',
      changePercent: -15.2,
      status: 'good',
      category: 'effectiveness'
    },
    {
      id: '6',
      name: 'Tỷ lệ tuân thủ An ninh HK',
      value: 98.5,
      target: 100,
      unit: '%',
      trend: 'stable',
      changePercent: 0.2,
      status: 'warning',
      category: 'safety'
    },
    {
      id: '7',
      name: 'Chứng chỉ IATA hợp lệ',
      value: 89,
      target: 95,
      unit: '%',
      trend: 'down',
      changePercent: -2.8,
      status: 'warning',
      category: 'compliance'
    },
    {
      id: '8',
      name: 'Tỷ lệ sử dụng ngân sách',
      value: 73,
      target: 85,
      unit: '%',
      trend: 'up',
      changePercent: 18.7,
      status: 'good',
      category: 'cost'
    }
  ]);

  const [drillDownData, setDrillDownData] = useState<DrillDownData[]>([
    {
      dimension: 'Theo Phòng ban',
      values: [
        { name: 'An ninh Hàng không', value: 96, percentage: 30, trend: 'up' },
        { name: 'Dịch vụ Khách hàng', value: 88, percentage: 23, trend: 'stable' },
        { name: 'Vận hành Sân bay', value: 92, percentage: 18, trend: 'up' },
        { name: 'Kỹ thuật & Bảo trì', value: 85, percentage: 15, trend: 'down' },
        { name: 'Quản lý Chuyến bay', value: 94, percentage: 8, trend: 'up' },
        { name: 'Hàng hóa & Logistics', value: 87, percentage: 6, trend: 'stable' }
      ]
    },
    {
      dimension: 'Theo Chi nhánh',
      values: [
        { name: 'Nội Bài - Hà Nội', value: 93, percentage: 33, trend: 'up' },
        { name: 'Tân Sơn Nhất - TP.HCM', value: 91, percentage: 29, trend: 'stable' },
        { name: 'Đà Nẵng', value: 89, percentage: 16, trend: 'up' },
        { name: 'Cam Ranh - Khánh Hòa', value: 88, percentage: 11, trend: 'stable' },
        { name: 'Phú Quốc - Kiên Giang', value: 92, percentage: 11, trend: 'up' }
      ]
    },
    {
      dimension: 'Theo Loại đào tạo',
      values: [
        { name: 'An ninh Hàng không', value: 96, percentage: 35, trend: 'up' },
        { name: 'Dịch vụ Khách hàng', value: 87, percentage: 25, trend: 'stable' },
        { name: 'Vận hành Kỹ thuật', value: 84, percentage: 20, trend: 'down' },
        { name: 'Chứng chỉ Quốc tế', value: 95, percentage: 15, trend: 'up' },
        { name: 'An toàn Lao động', value: 98, percentage: 5, trend: 'up' }
      ]
    },
    {
      dimension: 'Theo Chi phí',
      values: [
        { name: 'Dưới 10M', value: 91, percentage: 25, trend: 'up' },
        { name: '10M - 50M', value: 89, percentage: 40, trend: 'stable' },
        { name: '50M - 100M', value: 85, percentage: 25, trend: 'down' },
        { name: 'Trên 100M', value: 87, percentage: 10, trend: 'up' }
      ]
    }
  ]);

  const [alerts, setAlerts] = useState<Alert[]>([
    {
      id: '1',
      type: 'certification_expiry',
      title: 'Chứng chỉ An ninh sắp hết hạn',
      description: '87 chứng chỉ An ninh Hàng không sẽ hết hạn trong 30 ngày tới',
      severity: 'high',
      department: 'An ninh Hàng không',
      branch: 'Nội Bài - Hà Nội',
      date: '2025-01-15',
      status: 'active'
    },
    {
      id: '2',
      type: 'low_completion',
      title: 'Tỷ lệ hoàn thành thấp',
      description: 'Khóa "Xử lý Hàng hóa Nguy hiểm" chỉ đạt 68% tỷ lệ hoàn thành',
      severity: 'medium',
      department: 'Hàng hóa & Logistics',
      branch: 'Tân Sơn Nhất - TP.HCM',
      date: '2025-01-14',
      status: 'acknowledged'
    },
    {
      id: '3',
      type: 'budget_exceeded',
      title: 'Vượt ngân sách đào tạo Q1',
      description: 'Chi nhánh Đà Nẵng đã sử dụng 108% ngân sách đào tạo quý 1',
      severity: 'high',
      department: 'Vận hành Sân bay',
      branch: 'Đà Nẵng',
      date: '2025-01-13',
      status: 'active'
    },
    {
      id: '4',
      type: 'safety_compliance',
      title: 'Cần cập nhật quy trình An toàn',
      description: 'Phát hiện 3 quy trình an toàn cần được cập nhật theo tiêu chuẩn ICAO mới',
      severity: 'medium',
      department: 'Kỹ thuật & Bảo trì',
      branch: 'Cam Ranh - Khánh Hòa',
      date: '2025-01-12',
      status: 'active'
    },
    {
      id: '5',
      type: 'schedule_delay',
      title: 'Trễ lịch đào tạo IATA',
      description: 'Khóa "IATA Cargo Agent Training" bị trễ 2 tuần do chờ giảng viên quốc tế',
      severity: 'medium',
      department: 'Hàng hóa & Logistics',
      branch: 'Phú Quốc - Kiên Giang',
      date: '2025-01-11',
      status: 'active'
    },
    {
      id: '6',
      type: 'low_satisfaction',
      title: 'Điểm hài lòng thấp',
      description: 'Khóa "Vận hành Thiết bị Radar" chỉ đạt 3.4/5 điểm hài lòng',
      severity: 'medium',
      department: 'Quản lý Chuyến bay',
      branch: 'Nội Bài - Hà Nội',
      date: '2025-01-10',
      status: 'resolved'
    }
  ]);

  const [selectedPeriod, setSelectedPeriod] = useState('month');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedDimension, setSelectedDimension] = useState(0);

  const getKPIStatusColor = (status: string) => {
    switch (status) {
      case 'good': return 'text-green-600 bg-green-100';
      case 'warning': return 'text-yellow-600 bg-yellow-100';
      case 'critical': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'up': return <TrendingUp className="w-4 h-4 text-green-500" />;
      case 'down': return <TrendingDown className="w-4 h-4 text-red-500" />;
      default: return <Activity className="w-4 h-4 text-gray-500" />;
    }
  };

  const getAlertColor = (severity: string) => {
    switch (severity) {
      case 'high': return 'border-l-red-500 bg-red-50';
      case 'medium': return 'border-l-yellow-500 bg-yellow-50';
      case 'low': return 'border-l-blue-500 bg-blue-50';
      default: return 'border-l-gray-500 bg-gray-50';
    }
  };

  const getAlertStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'text-red-600 bg-red-100';
      case 'acknowledged': return 'text-yellow-600 bg-yellow-100';
      case 'resolved': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getAlertStatusText = (status: string) => {
    switch (status) {
      case 'active': return 'Đang xử lý';
      case 'acknowledged': return 'Đã xác nhận';
      case 'resolved': return 'Đã giải quyết';
      default: return status;
    }
  };

  const filteredKPIs = selectedCategory === 'all' ? kpis : kpis.filter(kpi => kpi.category === selectedCategory);
  const activeAlerts = alerts.filter(alert => alert.status === 'active');

  return (
    <div className="w-full min-h-screen bg-gray-50">
      <div className="w-full px-6 py-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Báo cáo & Thống kê Đào tạo</h1>
            <p className="text-gray-600">Dashboard KPI thời gian thực - Hệ thống Dịch vụ Sân bay</p>
          </div>
          <div className="flex gap-2">
            <button className="bg-green-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-green-700">
              <Download className="w-4 h-4" />
              Xuất báo cáo
            </button>
            <button className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700">
              <Filter className="w-4 h-4" />
              Bộ lọc
            </button>
          </div>
        </div>

        {/* Period and Category Filters */}
        <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
          <div className="flex gap-4">
            <div className="flex items-center space-x-2">
              <Calendar className="w-4 h-4 text-gray-500" />
              <select
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="week">Tuần này</option>
                <option value="month">Tháng này</option>
                <option value="quarter">Quý này</option>
                <option value="year">Năm này</option>
              </select>
            </div>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">Tất cả KPI</option>
              <option value="completion">Hoàn thành</option>
              <option value="cost">Chi phí</option>
              <option value="effectiveness">Hiệu quả</option>
              <option value="satisfaction">Hài lòng</option>
              <option value="safety">An toàn</option>
              <option value="compliance">Tuân thủ</option>
            </select>
          </div>
        </div>

        {/* Alerts Panel */}
        {activeAlerts.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                <AlertTriangle className="w-5 h-5 text-red-500 mr-2" />
                Cảnh báo Hệ thống ({activeAlerts.length})
              </h2>
            </div>
            <div className="space-y-3">
              {activeAlerts.map(alert => (
                <div key={alert.id} className={`border-l-4 p-4 rounded-r-lg ${getAlertColor(alert.severity)}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <h3 className="font-medium text-gray-900">{alert.title}</h3>
                        <span className={`px-2 py-1 text-xs rounded-full ${getAlertStatusColor(alert.status)}`}>
                          {getAlertStatusText(alert.status)}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">{alert.description}</p>
                      <div className="flex items-center space-x-4 text-xs text-gray-500">
                        <span>{alert.department}</span>
                        <span>📍 {alert.branch}</span>
                        <span>{alert.date}</span>
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <button className="text-blue-600 hover:text-blue-800 text-sm">Chi tiết</button>
                      <button className="text-green-600 hover:text-green-800 text-sm">Xử lý</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* KPI Dashboard */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-4 gap-6 mb-6">
          {filteredKPIs.map(kpi => (
            <div key={kpi.id} className="bg-white rounded-lg shadow-sm border p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-gray-500 mb-1">{kpi.name}</h3>
                  <div className="flex items-baseline space-x-2">
                    <span className="text-2xl font-bold text-gray-900">
                      {kpi.unit === 'VND' ? `${(kpi.value / 1000000).toFixed(1)}M` : `${kpi.value}${kpi.unit}`}
                    </span>
                    <span className="text-sm text-gray-500">
                      / {kpi.unit === 'VND' ? `${(kpi.target / 1000000).toFixed(1)}M` : `${kpi.target}${kpi.unit}`}
                    </span>
                  </div>
                </div>
                <span className={`px-2 py-1 text-xs rounded-full ${getKPIStatusColor(kpi.status)}`}>
                  {kpi.status === 'good' ? 'Tốt' : kpi.status === 'warning' ? 'Cảnh báo' : 'Nghiêm trọng'}
                </span>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  {getTrendIcon(kpi.trend)}
                  <span className={`text-sm font-medium ${
                    kpi.trend === 'up' ? 'text-green-600' : 
                    kpi.trend === 'down' ? 'text-red-600' : 'text-gray-600'
                  }`}>
                    {kpi.changePercent > 0 ? '+' : ''}{kpi.changePercent}%
                  </span>
                </div>
                <div className="text-xs text-gray-500">vs tháng trước</div>
              </div>
              
              <div className="mt-4">
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full ${
                      kpi.status === 'good' ? 'bg-green-500' :
                      kpi.status === 'warning' ? 'bg-yellow-500' : 'bg-red-500'
                    }`}
                    style={{ width: `${Math.min((kpi.value / kpi.target) * 100, 100)}%` }}
                  ></div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Drill-down Analysis */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                <BarChart3 className="w-5 h-5 text-blue-500 mr-2" />
                Phân tích đa chiều
              </h2>
              <select
                value={selectedDimension}
                onChange={(e) => setSelectedDimension(Number(e.target.value))}
                className="text-sm px-3 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              >
                {drillDownData.map((dimension, index) => (
                  <option key={index} value={index}>{dimension.dimension}</option>
                ))}
              </select>
            </div>
            
            <div className="space-y-3">
              {drillDownData[selectedDimension]?.values.map((item, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">{item.name}</div>
                      <div className="text-sm text-gray-500">{item.percentage}% tổng số</div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="font-medium text-gray-900">{item.value}%</span>
                    {getTrendIcon(item.trend)}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center mb-4">
              <PieChart className="w-5 h-5 text-green-500 mr-2" />
              Phân bố ngân sách đào tạo
            </h2>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Đã sử dụng</span>
                <span className="font-medium text-red-600">1.1B VND (73%)</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div className="bg-red-500 h-3 rounded-full" style={{ width: '73%' }}></div>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Đã phân bổ</span>
                <span className="font-medium text-yellow-600">0.3B VND (20%)</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div className="bg-yellow-500 h-3 rounded-full" style={{ width: '20%' }}></div>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Còn lại</span>
                <span className="font-medium text-green-600">0.1B VND (7%)</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div className="bg-green-500 h-3 rounded-full" style={{ width: '7%' }}></div>
              </div>
            </div>
            
            <div className="mt-6 p-4 bg-blue-50 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <Target className="w-4 h-4 text-blue-600" />
                <span className="font-medium text-blue-900">Dự báo & Khuyến nghị</span>
              </div>
              <p className="text-sm text-blue-800">
                Tỷ lệ sử dụng ngân sách cao hơn kế hoạch. Cần ưu tiên đào tạo An ninh Hàng không 
                và các chứng chỉ bắt buộc cho Q2. Khuyến nghị bổ sung 200M cho đào tạo ICAO.
              </p>
            </div>
          </div>
        </div>

        {/* Performance Trends */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Xu hướng hiệu suất chuyên ngành</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600 mb-2">94%</div>
              <div className="text-sm text-gray-600 mb-1">Tỷ lệ hoàn thành An ninh</div>
              <div className="text-xs text-green-600">+3% so với quý trước</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-green-600 mb-2">4.8/5</div>
              <div className="text-sm text-gray-600 mb-1">Điểm hài lòng TB</div>
              <div className="text-xs text-green-600">+0.4 so với quý trước</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-purple-600 mb-2">215%</div>
              <div className="text-sm text-gray-600 mb-1">ROI đào tạo</div>
              <div className="text-xs text-green-600">+18% so với quý trước</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-orange-600 mb-2">98.5%</div>
              <div className="text-sm text-gray-600 mb-1">Tuân thủ An toàn</div>
              <div className="text-xs text-yellow-600">Duy trì mức cao</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportsPage; 